import { Button } from '@bluebeam/react-skyline-button';
import { useTranslator } from '@bluebeam/react-i18n';
import { useAuth } from 'auth';
import classNames from 'classnames';
import LoadingAnimation from 'components/LoadingAnimation';
import EditProjectModal from 'components/modals/EditProjectModal';
import FileManagerModal from 'components/modals/FileManagerModal';
import Navigation from 'components/Navigation';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import {
  ManageDrawingsSupportContext,
  ManageDrawingsSupportValue,
} from 'providers/ManageDrawingsSupportProvider';
import React, { ReactElement, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ActiveExport } from 'records/Export';
import { CreatePunch } from 'records/interactions/CreatePunch';
import { CreateRFI } from 'records/interactions/CreateRFI';
import { ModalState } from 'records/ModalState';
import { NewPunch } from 'records/NewPunch';
import { NewRFI } from 'records/NewRFI';
import { getFile } from 'services/fileService';
import { Project } from 'records/Project';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { getProject } from 'services/projectService';
import { SheetQuery } from 'records/SheetQuery';
import Drawings from './Drawings';
import './ProjectDashboard.css';
import ProjectInfo from './ProjectInfo';
import Punch from './Punch';
import RFIs from './RFIs';

interface Props extends WithAppStoreProps, RouteComponentProps {}

const ProjectDashboard = (props: Props): ReactElement => {
  const { appState, actions, history, location } = props;
  const { userId } = useAuth();
  const { projects, selectedProject, users } = appState;
  const {
    addActiveExport,
    createInteraction,
    createModal,
    getProjectGroups,
    initRFIQuery,
  } = actions;
  const project = projects.get(selectedProject);

  const [pagesLoaded, setPagesLoaded] = useState<boolean>(false);
  const [projectData, setProjectData] = useState<Project | null>(null);
  const { translate } = useTranslator(['roverDashboard', 'loading']);

  const {
    [FeatureFlag.EssentialsDrawingEditor]: isEssentialsDrawingEditorEnabled,
    [FeatureFlag.EssentialsPunchPage]: punchesPageEnabled,
  } = useFeatureFlags();

  const getProjectData = async () => {
    const projectData = await getProject(
      appState.selectedProject,
      appState.userLicense.package
    );

    setProjectData(projectData);
  };

  const loadQueries = async (): Promise<void> => {
    await getProjectGroups();
  };

  useEffect(() => {
    getProjectData();
  }, []);

  useEffect(() => {
    !pagesLoaded && loadQueries();

    const queryParams = new URLSearchParams(location.search);
    const downloadQueryParam = queryParams.get('fileid');
    if (downloadQueryParam) {
      const setExports = async (): Promise<void> => {
        const resp = await getFile(selectedProject, downloadQueryParam);
        const activeExport = new ActiveExport({
          fileid: resp.fileid,
          filename: resp.filename,
          url: resp.url,
        });

        addActiveExport(ActiveExport.deserialize(activeExport));
      };

      setExports();

      const modalState = new ModalState({
        id: 'fileManagerModal',
        portal: 'main',
        open: true,
        component: FileManagerModal,
      });

      createModal(modalState);
    }

    setPagesLoaded(true);

    return function cleanup() {
      setPagesLoaded(false);
    };
  });

  const handleRFIViewAll = async (projectId: string): Promise<void> => {
    await initRFIQuery();
    history.push(`/project/${projectId}/rfis`);
  };

  const handlePunchViewAll = (projectId: string): void => {
    history.push(`/project/${projectId}/punches`);
  };

  const handleDrawingsViewAll = (projectId: string): void => {
    history.push(`/project/${projectId}/drawings/published`);
  };

  const handleEditProjectInfo = async (): Promise<void> => {
    history.push(`/project/${project?.id}/settings/edit-details`);
  };

  const handleViewAllProjectsClicked = async (): Promise<void> => {
    await actions.setSheetQuery(new SheetQuery());
    history.push(`/projects`);
  };

  const handleCreatePunchItemClicked = (projectId: string): void => {
    if (punchesPageEnabled) {
      history.push(`/project/${projectId}/punches?punch=0`);
    } else {
      history.push(`/project/${projectId}/punches`);

      const newCreatePunch = new CreatePunch({
        punch: new NewPunch({
          // Might need to change this to be set when dialog is opened.
          projectid: selectedProject,
          clientcreateddate: Math.floor(Date.now() / 1000),
        }),
      });

      createInteraction(newCreatePunch);
    }
  };

  const handleCreateRFIClicked = (projectId: string): void => {
    const currentUser = users.find((u) => userId === u.id);
    const now = new Date();

    const interaction = new CreateRFI({
      rfi: new NewRFI({
        projectid: selectedProject,
        duedate: Math.floor(now.setDate(now.getDate() + 3) / 1000),
        submittingname: `${currentUser?.profile.firstname} ${currentUser?.profile.lastname}`,
        submittingemail: currentUser?.email,
        submittingcompany: currentUser?.profile.company || ' ',
      }),
    });

    createInteraction(interaction);
    history.push(`/project/${projectId}/rfis`);
  };

  const handleSheetClicked = async (sheetId: string): Promise<void> => {
    if (isEssentialsDrawingEditorEnabled) {
      history.push(
        `/project/${appState.selectedProject}/drawing/${sheetId}/editor`
      );
    } else {
      history.push(
        `/project/${appState.selectedProject}/sheet/${sheetId}/view`
      );
    }
  };

  const handleViewTeamClicked = async (): Promise<void> => {
    history.push(`/project/${project?.id}/settings/users`);
  };

  return (
    <>
      <Navigation
        topNavItems={(): React.ReactNode => (
          <>
            <Button
              onClick={handleViewAllProjectsClicked}
              variant="text"
              id="project-dashboard-view-all-projects"
              className="ml3"
            >
              {translate('roverDashboard:viewAllProjects')}
            </Button>
          </>
        )}
      >
        {!pagesLoaded ? (
          <div className="flex-center project-dashboard__loading-animation">
            <div className="column items-center">
              <LoadingAnimation
                pageName={translate('loading:loadingDashboard')}
              />
            </div>
          </div>
        ) : (
          <div id="project-dashboard-page" className={classNames('oya grow-1')}>
            <div className="bdg-gray-2">
              <div className="flex column px4 mb4">
                <ProjectInfo
                  onEditClicked={handleEditProjectInfo}
                  onViewTeamClicked={handleViewTeamClicked}
                  projectData={projectData}
                />

                <ManageDrawingsSupportContext.Consumer>
                  {({ onSuccess }: ManageDrawingsSupportValue) => (
                    <Drawings
                      onViewAllClicked={handleDrawingsViewAll}
                      onSheetClicked={(sheet) => {
                        onSuccess(() => handleSheetClicked(sheet));
                      }}
                    />
                  )}
                </ManageDrawingsSupportContext.Consumer>

                <div className="w100 h100 shrink-0">
                  <div className="mo-column lu-row justify-start w100 h100 shrink-0">
                    <Punch
                      onViewAllClicked={handlePunchViewAll}
                      onCreatePunchItemClicked={handleCreatePunchItemClicked}
                    />
                    <div className="lu-hide mb5 shrink-0" />
                    <div className="lu-show wrem-1p5 shrink-0" />
                    <RFIs
                      onViewAllClicked={handleRFIViewAll}
                      onCreateRFIClicked={handleCreateRFIClicked}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Navigation>
    </>
  );
};

export default withAppStore<Props>(ProjectDashboard);
